import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form, Input, Select, Button, Modal, message, Typography, Card, Divider,
  Row, Col, InputNumber, Tag, Empty, Skeleton
} from 'antd';
import {
  WarningOutlined, CameraOutlined, VideoCameraOutlined, EnvironmentOutlined,
  PhoneOutlined, UserOutlined, SendOutlined, PlusOutlined,
  SafetyCertificateOutlined, DeleteOutlined, AimOutlined, LoadingOutlined,
  ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  EyeOutlined, FileImageOutlined
} from '@ant-design/icons';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import usePublicStore from '../../store/publicStore';
import { isAuth, getUser } from '../../utils/auth';
import api from '../../api/axios';
import 'leaflet/dist/leaflet.css';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function LocationPicker({ position, onPick }) {
  useMapEvents({ click(e) { onPick([e.latlng.lat, e.latlng.lng]); } });
  return position ? <Marker position={position} icon={redIcon} /> : null;
}

function FlyTo({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, 16, { duration: 1 }); }, [center, map]);
  return null;
}

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const P = '#5F33E1';

const STATUS_MAP = {
  new:      { label: "Yangi",            color: 'blue',    icon: <ClockCircleOutlined /> },
  pending:  { label: "Yangi",            color: 'blue',    icon: <ClockCircleOutlined /> },
  reviewed: { label: "Ko'rib chiqildi",  color: 'green',   icon: <CheckCircleOutlined /> },
  approved: { label: "Ko'rib chiqildi",  color: 'green',   icon: <CheckCircleOutlined /> },
  rejected: { label: "Rad etildi",       color: 'red',     icon: <CloseCircleOutlined /> },
};

const getStatus = (s) => STATUS_MAP[s] || STATUS_MAP.new;

export default function ReportPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [mapPosition, setMapPosition] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const {
    regions, districts, serviceTypes,
    fetchRegions, fetchServiceTypes, fetchDistricts
  } = usePublicStore();

  const user = getUser();

  // Fetch user's reports
  const fetchReports = () => {
    setReportsLoading(true);
    api.get('/reports/my').then(res => {
      setReports(res.data?.data || res.data || []);
    }).catch(() => {
      setReports([]);
    }).finally(() => {
      setReportsLoading(false);
    });
  };

  useEffect(() => {
    if (!isAuth()) {
      message.warning("Bu sahifaga kirish uchun tizimga kiring");
      navigate('/auth');
      return;
    }
    fetchRegions();
    fetchServiceTypes();
    fetchReports();
  }, []);

  useEffect(() => {
    if (selectedRegion) fetchDistricts(selectedRegion);
  }, [selectedRegion]);

  const handleMapPick = (pos) => {
    setMapPosition(pos);
    form.setFieldsValue({ lat: parseFloat(pos[0].toFixed(6)), long: parseFloat(pos[1].toFixed(6)) });
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) { message.error("Brauzeringiz joylashuvni aniqlay olmaydi"); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = [pos.coords.latitude, pos.coords.longitude];
        setMapPosition(coords);
        setFlyTarget(coords);
        form.setFieldsValue({ lat: parseFloat(coords[0].toFixed(6)), long: parseFloat(coords[1].toFixed(6)) });
        setGeoLoading(false);
        message.success("Joylashuv aniqlandi!");
      },
      () => { setGeoLoading(false); message.error("Joylashuvni aniqlab bo'lmadi"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleCapturePhoto = () => { if (fileInputRef.current) fileInputRef.current.click(); };
  const handleCaptureVideo = () => { if (videoInputRef.current) videoInputRef.current.click(); };

  const handleFileChange = (e, type) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (type === 'photo' && !file.type.startsWith('image/')) { message.error('Faqat rasm'); return; }
      if (type === 'video' && !file.type.startsWith('video/')) { message.error('Faqat video'); return; }
      if (file.size > 50 * 1024 * 1024) { message.error('50MB dan oshmasligi kerak'); return; }
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      setMediaFiles(prev => [...prev, {
        id: Date.now() + Math.random(), file,
        type: file.type.startsWith('image/') ? 'photo' : 'video',
        name: file.name, preview,
      }]);
    });
    e.target.value = '';
  };

  const removeMedia = (id) => {
    setMediaFiles(prev => {
      const item = prev.find(m => m.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter(m => m.id !== id);
    });
  };

  const handleSubmit = async (values) => {
    if (mediaFiles.length === 0) { message.warning("Kamida bitta isbot yuklang"); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('vendor_name', values.vendor_name);
      formData.append('service_type_id', values.service_type_id);
      formData.append('region_id', values.region_id);
      formData.append('district_id', values.district_id);
      formData.append('address', values.address);
      formData.append('lat', values.lat || '');
      formData.append('long', values.long || '');
      formData.append('reporter_phone', values.reporter_phone);
      formData.append('details', values.details);
      formData.append('reporter_user_id', user?.id || '');
      mediaFiles.forEach((media, i) => { formData.append(`evidence[${i}]`, media.file); });

      await api.post('/reports/illegal-vendor', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      message.success("Xabar muvaffaqiyatli yuborildi!");
      form.resetFields();
      setMediaFiles([]);
      setSelectedRegion(null);
      setMapPosition(null);
      setFlyTarget(null);
      setModalOpen(false);
      fetchReports();
    } catch (err) {
      message.error(err.response?.data?.message || "Xabar yuborishda xatolik");
    } finally {
      setSubmitting(false);
    }
  };

  const openModal = () => {
    form.resetFields();
    form.setFieldsValue({ reporter_phone: user?.phone || '' });
    setMediaFiles([]);
    setSelectedRegion(null);
    setMapPosition(null);
    setFlyTarget(null);
    setModalOpen(true);
  };

  if (!isAuth()) return null;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
      {/* Hidden camera inputs */}
      <input type="file" ref={fileInputRef} accept="image/*" capture="environment"
        style={{ display: 'none' }} onChange={(e) => handleFileChange(e, 'photo')} />
      <input type="file" ref={videoInputRef} accept="video/*" capture="environment"
        style={{ display: 'none' }} onChange={(e) => handleFileChange(e, 'video')} />

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{
          width: 64, height: 64, borderRadius: 20, margin: '0 auto 16px',
          background: 'linear-gradient(135deg, #FF6B6B, #EE5A24)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(238,90,36,0.3)',
        }}>
          <SafetyCertificateOutlined style={{ color: '#fff', fontSize: 30 }} />
        </div>
        <Title level={2} style={{ margin: '0 0 8px' }}>Hamkor — Xabar berish</Title>
        <Paragraph type="secondary" style={{ maxWidth: 500, margin: '0 auto 20px', fontSize: 14 }}>
          Noqonuniy xizmat ko'rsatayotgan tadbirkorlar haqida soliq organlariga xabar bering.
          Barcha ma'lumotlar maxfiy saqlanadi.
        </Paragraph>

        {/* Main CTA button */}
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={openModal}
          style={{
            height: 52, borderRadius: 14, fontSize: 16, fontWeight: 700,
            background: 'linear-gradient(135deg, #EE5A24, #FF6B6B)', border: 'none',
            boxShadow: '0 6px 24px rgba(238,90,36,0.3)', padding: '0 40px',
          }}
        >
          Yangi xabar yuborish
        </Button>
      </div>

      {/* Reports list */}
      <div style={{ marginTop: 8 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16,
        }}>
          <Title level={4} style={{ margin: 0 }}>
            <FileImageOutlined style={{ color: P, marginRight: 8 }} />
            Yuborilgan xabarlar
          </Title>
          <Tag style={{ borderRadius: 10 }}>{reports.length} ta</Tag>
        </div>

        {reportsLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[1, 2, 3].map(i => <Skeleton key={i} active paragraph={{ rows: 2 }} />)}
          </div>
        ) : reports.length === 0 ? (
          <Card bordered={false} style={{ borderRadius: 16, textAlign: 'center', padding: '40px 20px' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary">
                  Hali birorta ham xabar yuborilmagan.
                  <br />"Yangi xabar yuborish" tugmasini bosing.
                </Text>
              }
            />
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {reports.map((r, idx) => {
              const st = getStatus(r.status);
              return (
                <Card
                  key={r.id || idx}
                  bordered={false}
                  style={{
                    borderRadius: 14,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                    border: '1px solid #f0f0f0',
                    overflow: 'hidden',
                  }}
                  styles={{ body: { padding: '14px 18px' } }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        <Text strong style={{ fontSize: 15 }}>{r.vendor_name || 'Noma\'lum'}</Text>
                        <Tag
                          icon={st.icon}
                          color={st.color}
                          style={{ borderRadius: 8, fontSize: 11, margin: 0 }}
                        >
                          {st.label}
                        </Tag>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                        {r.service_type?.name && (
                          <Tag style={{ borderRadius: 6, fontSize: 11, margin: 0 }}>{r.service_type.name}</Tag>
                        )}
                        {(r.region?.nameUz || r.address) && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            <EnvironmentOutlined style={{ marginRight: 3 }} />
                            {r.region?.nameUz}{r.district?.nameUz ? `, ${r.district.nameUz}` : ''}{r.address ? ` — ${r.address}` : ''}
                          </Text>
                        )}
                      </div>
                      {r.details && (
                        <Paragraph
                          type="secondary"
                          ellipsis={{ rows: 2 }}
                          style={{ fontSize: 13, marginBottom: 4, lineHeight: 1.6 }}
                        >
                          {r.details}
                        </Paragraph>
                      )}
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        <ClockCircleOutlined style={{ marginRight: 3 }} />
                        {r.created_at ? new Date(r.created_at).toLocaleDateString('uz-UZ', {
                          year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
                        }) : '—'}
                      </Text>
                    </div>
                    {/* Evidence thumbnails */}
                    {r.evidence?.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {r.evidence.slice(0, 3).map((ev, i) => (
                          <div key={i} style={{
                            width: 48, height: 48, borderRadius: 8, overflow: 'hidden',
                            background: '#f0f0f0', border: '1px solid #e8e8e8',
                          }}>
                            {ev.type === 'photo' || ev.file_path?.match(/\.(jpg|jpeg|png|gif|webp)/i) ? (
                              <img
                                src={ev.url || (process.env.REACT_APP_URL + ev.file_path)}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div style={{
                                width: '100%', height: '100%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'linear-gradient(135deg,#1890ff,#096dd9)', color: '#fff',
                              }}>
                                <VideoCameraOutlined style={{ fontSize: 18 }} />
                              </div>
                            )}
                          </div>
                        ))}
                        {r.evidence.length > 3 && (
                          <div style={{
                            width: 48, height: 48, borderRadius: 8, background: 'var(--qk-bg-soft)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, color: '#999', border: '1px solid #e8e8e8',
                          }}>
                            +{r.evidence.length - 3}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Rejection reason */}
                  {r.status === 'rejected' && r.rejection_reason && (
                    <div style={{
                      marginTop: 10, padding: '8px 12px', background: 'rgba(255,77,79,0.06)',
                      borderRadius: 8, border: '1px solid rgba(255,77,79,0.15)',
                    }}>
                      <Text type="danger" style={{ fontSize: 12 }}>
                        <CloseCircleOutlined style={{ marginRight: 4 }} />
                        Sabab: {r.rejection_reason}
                      </Text>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Info footer */}
      <div style={{
        textAlign: 'center', marginTop: 24, padding: '16px 20px',
        background: 'rgba(95,51,225,0.04)', borderRadius: 14,
        border: '1px solid rgba(95,51,225,0.08)',
      }}>
        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.8 }}>
          <SafetyCertificateOutlined style={{ color: P, marginRight: 4 }} />
          Ma'lumotlaringiz maxfiy saqlanadi. Xabar soliq organlari tomonidan ko'rib chiqiladi.
          <br />Noto'g'ri xabar berish qonunga muvofiq javobgarlikka sabab bo'lishi mumkin.
        </Text>
      </div>

      {/* ===== MODAL — Report Form ===== */}
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={720}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <WarningOutlined style={{ color: '#EE5A24', fontSize: 20 }} />
            <span>Noqonuniy xizmat haqida xabar</span>
          </div>
        }
        footer={null}
        destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto', padding: '20px 24px' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          initialValues={{ reporter_phone: user?.phone || '' }}
        >
          {/* Vendor info */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
            padding: '8px 14px', background: 'rgba(238,90,36,0.06)', borderRadius: 10,
          }}>
            <WarningOutlined style={{ color: '#EE5A24', fontSize: 16 }} />
            <Text strong style={{ color: '#EE5A24', fontSize: 13 }}>Xizmat ko'rsatuvchi haqida</Text>
          </div>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item name="vendor_name" label="Nomi" rules={[{ required: true, message: 'Nomini kiriting' }]}>
                <Input prefix={<UserOutlined style={{ color: '#bbb' }} />} placeholder="Masalan: Fayz to'yxonasi" style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="service_type_id" label="Turi" rules={[{ required: true, message: 'Turini tanlang' }]}>
                <Select placeholder="Xizmat turi" options={serviceTypes.map(t => ({ value: t.id, label: t.name }))} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item name="region_id" label="Viloyat" rules={[{ required: true, message: 'Tanlang' }]}>
                <Select
                  placeholder="Viloyat"
                  options={regions.map(r => ({ value: r.id, label: r.nameUz }))}
                  onChange={(val) => { setSelectedRegion(val); form.setFieldsValue({ district_id: undefined }); }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="district_id" label="Tuman" rules={[{ required: true, message: 'Tanlang' }]}>
                <Select
                  placeholder={selectedRegion ? "Tuman" : "Avval viloyat tanlang"}
                  disabled={!selectedRegion}
                  options={districts.map(d => ({ value: d.id, label: d.nameUz }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="address" label="Manzil" rules={[{ required: true, message: 'Kiriting' }]}>
            <Input prefix={<EnvironmentOutlined style={{ color: '#bbb' }} />} placeholder="To'liq manzil" style={{ borderRadius: 10 }} />
          </Form.Item>

          {/* Map picker */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
              <Text strong style={{ fontSize: 13 }}>
                <EnvironmentOutlined style={{ color: '#EE5A24', marginRight: 4 }} />
                Koordinata
              </Text>
              <Button
                icon={geoLoading ? <LoadingOutlined /> : <AimOutlined />}
                loading={geoLoading}
                size="small"
                onClick={handleGetLocation}
                style={{ borderRadius: 8, background: '#1890ff', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12 }}
              >
                Mening joylashuvim
              </Button>
            </div>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '2px solid #f0f0f0', height: 220, position: 'relative' }}>
              <MapContainer center={[41.311081, 69.240562]} zoom={6} style={{ width: '100%', height: '100%' }}>
                <TileLayer attribution='&copy; OSM' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <LocationPicker position={mapPosition} onPick={handleMapPick} />
                {flyTarget && <FlyTo center={flyTarget} />}
              </MapContainer>
              {!mapPosition && (
                <div style={{
                  position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
                  background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '4px 14px',
                  borderRadius: 16, fontSize: 11, zIndex: 1000, pointerEvents: 'none',
                }}>
                  Xaritani bosib belgilang
                </div>
              )}
            </div>
            <Row gutter={12} style={{ marginTop: 8 }}>
              <Col xs={12}>
                <Form.Item name="lat" style={{ marginBottom: 0 }}>
                  <InputNumber placeholder="lat" size="small" style={{ width: '100%', borderRadius: 8 }} readOnly />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item name="long" style={{ marginBottom: 0 }}>
                  <InputNumber placeholder="long" size="small" style={{ width: '100%', borderRadius: 8 }} readOnly />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Divider style={{ margin: '8px 0 16px' }} />

          {/* Reporter info */}
          <Form.Item name="reporter_phone" label="Telefon raqamingiz" rules={[{ required: true, message: 'Kiriting' }]}>
            <Input prefix={<PhoneOutlined style={{ color: '#bbb' }} />} placeholder="+998 90 123 45 67" style={{ borderRadius: 10 }} />
          </Form.Item>

          <Form.Item name="details" label="Batafsil ma'lumot" rules={[{ required: true, message: 'Kiriting' }]}>
            <TextArea rows={3} placeholder="Noqonuniy faoliyat haqida batafsil..." style={{ borderRadius: 10 }} maxLength={2000} showCount />
          </Form.Item>

          <Divider style={{ margin: '8px 0 16px' }} />

          {/* Camera evidence */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '6px 12px', background: 'rgba(82,196,26,0.06)', borderRadius: 10 }}>
            <CameraOutlined style={{ color: '#52c41a', fontSize: 16 }} />
            <Text strong style={{ color: '#52c41a', fontSize: 13 }}>Isbot (faqat kameradan)</Text>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <Button icon={<CameraOutlined />} onClick={handleCapturePhoto}
              style={{ borderRadius: 10, background: '#52c41a', color: '#fff', border: 'none', fontWeight: 600 }}>
              Suratga olish
            </Button>
            <Button icon={<VideoCameraOutlined />} onClick={handleCaptureVideo}
              style={{ borderRadius: 10, background: '#1890ff', color: '#fff', border: 'none', fontWeight: 600 }}>
              Video yozish
            </Button>
          </div>

          {mediaFiles.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8, marginBottom: 16 }}>
              {mediaFiles.map(media => (
                <div key={media.id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '2px solid #f0f0f0' }}>
                  {media.type === 'photo' && media.preview ? (
                    <img src={media.preview} alt="" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div style={{ width: '100%', height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1890ff', color: '#fff' }}>
                      <VideoCameraOutlined style={{ fontSize: 24 }} />
                    </div>
                  )}
                  <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => removeMedia(media.id)}
                    style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,0.9)', borderRadius: 6, width: 24, height: 24 }} />
                </div>
              ))}
            </div>
          )}

          {/* Submit */}
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={submitting}
            icon={<SendOutlined />}
            disabled={mediaFiles.length === 0}
            style={{
              height: 48, borderRadius: 12, fontSize: 15, fontWeight: 700,
              background: mediaFiles.length > 0 ? 'linear-gradient(135deg, #EE5A24, #FF6B6B)' : undefined,
              border: 'none',
            }}
          >
            Xabar yuborish
          </Button>
          {mediaFiles.length === 0 && (
            <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 6, fontSize: 11 }}>
              Yuborish uchun kamida bitta isbot kerak
            </Text>
          )}
        </Form>
      </Modal>
    </div>
  );
}
